import { useState } from 'react';
import { friendService } from '../../services';

/**
 * The chip on the right of a search result row.
 *
 * Three kinds of chip look identical by design, and that was the bug: ADD was
 * rendered as an inert <span> beside a MESSAGE chip that navigates, so it read
 * as a button, invited a tap, and did nothing at all.
 *
 *   - a destination (MESSAGE, TICKETS, JOIN…) → navigates
 *   - ADD on a person                         → sends the friend request here,
 *                                               in place, and becomes SENT
 *   - anything else (PENDING, a price)        → genuinely a label, and now the
 *                                               only thing that renders as one
 *
 * Sending in place rather than routing to the profile is the point: the row is
 * one of forty-eight, and making someone open a profile and come back to add
 * each person is why nobody would.
 */
export default function SearchActionChip({ item, onNavigate, variant = 'page' }) {
  const [state, setState] = useState('idle'); // idle | sending | sent | failed
  const [failure, setFailure] = useState('');

  // The overlay sits on the dark sheet and runs smaller, so it inherits its
  // colour rather than hard-coding black.
  const overlay = variant === 'overlay';
  const base = overlay
    ? 'flex-shrink-0 border-2 border-current px-[7px] py-[3px] font-mono text-[9px] font-bold uppercase tracking-[0.12em]'
    : 'flex-shrink-0 border-2 border-black px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.12em]';
  const interactive = overlay
    ? `${base} transition-colors hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-current`
    : `${base} text-black transition-colors hover:bg-black hover:text-white focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1`;
  const label = overlay ? `${base} opacity-60` : `${base} text-hand-muted`;

  // A chip that names its own destination is a link, whatever its text.
  if (item.actionHref) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onNavigate(item.actionHref); }}
        aria-label={`${item.action} ${item.title}`}
        className={interactive}
      >
        {item.action}
      </button>
    );
  }

  const canAdd = item.kind === 'person' && item.action === 'ADD' && item.id;
  if (!canAdd) {
    return <span className={label}>{item.action}</span>;
  }

  if (state === 'sent') return <span className={label}>Sent</span>;

  const send = async (e) => {
    e.stopPropagation();
    if (state === 'sending') return;
    setState('sending');
    setFailure('');
    try {
      const r = await friendService.sendFriendRequest(item.id);
      // The service resolves either way, so trust its flag over the absence of
      // a throw. A request that already exists is a success from here: the point
      // was to be connected to this person, and one is already on its way.
      if (r?.success === false && !/already/i.test(r?.error || '')) {
        setState('failed');
        setFailure(r.error || 'Could not send that.');
        return;
      }
      setState('sent');
    } catch (err) {
      const msg = err?.response?.data?.error?.message || '';
      if (/already/i.test(msg)) { setState('sent'); return; }
      setState('failed');
      setFailure(msg || 'Could not send that.');
    }
  };

  return (
    <button
      type="button"
      onClick={send}
      disabled={state === 'sending'}
      aria-label={`Add ${item.title}`}
      title={failure || undefined}
      className={`${interactive} ${state === 'failed' ? 'border-red-500 text-red-500' : ''} disabled:opacity-50`}
    >
      {state === 'sending' ? '…' : state === 'failed' ? 'Retry' : item.action}
    </button>
  );
}
